import './testAttachmentsEditComponent.scss'
import React, {useCallback, useRef, useState} from 'react';
import InputComponent from "../../../../../shared/components/core/input/InputComponent";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import {CommonService} from "../../../../../shared/services";
import FeedbackComponent from "../../../../../shared/components/feedback/feedbackComponent";
import {IAPIResponseType} from "../../../../../shared/models/api.model";
import _ from "lodash";
import {ImageConfig} from "../../../../../constants";
import DialogComponent from "../../../../../shared/components/dialog/dialogComponent";
import FilePreviewerComponent from "../../../../../shared/components/file-previewer/filePreviewerComponent";
import AttachmentViewDownloadComponent
    from "../../../../../shared/components/core/attachment-view-download/attachmentViewDownloadComponent";
import PdfPreviewComponent from "../../../../../shared/components/pdf-preview/pdfPreviewComponent";
import ErrorComponent from "../../../../../shared/components/form-controls/error/errorComponent";

interface TestAttachmentsEditComponentProps {
    testAttachments: any;
    onUpdateComplete: Function;
    testID: number;
    onEditCancel: Function;
}

const TestAttachmentsEditComponent = (props: TestAttachmentsEditComponentProps) => {
        const {testAttachments, onUpdateComplete, testID, onEditCancel} = props

        const [attachmentTitle, setAttachmentTitle] = useState<string>("");
        const attachmentFileRef = useRef<HTMLInputElement | null>(null);
        const [attachmentList, setAttachmentList] = useState<any>(testAttachments);
        const [showAttachmentPreviewDialog, setShowAttachmentPreviewDialog] = useState<boolean>(false);
        const [isEdited, setIsEdited] = useState<boolean>(false);
        const [isAttachmentBeingUploaded, setIsAttachmentBeingUploaded] = useState<boolean>(false);
        const [attachmentFile, setAttachmentFile] = useState<File | undefined>(undefined);
        const [isNewAttachment, setNewAttachment] = useState<boolean>(false)
        const [isContinueVisible, setIsContinueVisible] = useState<boolean>(false);

        const handleAttachmentSuccess = useCallback((response: any) => {
            CommonService._alert.showToast(response?.message || 'Added Patient Attachments', "success");
            setIsEdited(true);
            setAttachmentList((oldState: any) => {
                if (oldState?.length > 0) {
                    return [response.data, ...oldState]
                } else {
                    return [response.data]
                }
            })
            setIsAttachmentBeingUploaded(false);
            setAttachmentTitle("");
            setAttachmentFile(undefined);
            setShowAttachmentPreviewDialog(false);
        }, []);


        const onAttachmentSubmit = useCallback(() => {
            const formData = new FormData();
            if (attachmentFile) {
                formData.append('image', attachmentFile);
            }
            formData.append('attachmentName', attachmentTitle);
            setIsAttachmentBeingUploaded(true);
            CommonService._test.addTestAttachment(testID, formData)
                .then((response: IAPIResponseType<any>) => {
                    handleAttachmentSuccess(response);
                })
                .catch((error: any) => {
                    CommonService._alert.showToast(error?.message || 'Error uploading test attachment', "error");
                    setIsAttachmentBeingUploaded(false);
                })
        }, [handleAttachmentSuccess, attachmentTitle, attachmentFile, testID]);

        const handleAttachmentUpload = useCallback((event: any) => {
            const file = event?.target?.files[0];
            const maxAllowedSize = 5 * 1024 * 1024;
            if (event?.target?.files[0].size > maxAllowedSize) {
                CommonService._alert.showToast('File size should be under 5MB.', "error");
                event.target.value = '';
                return;
            }
            event.target.value = null;
            event.target.files = null;
            setIsContinueVisible(true);
            setShowAttachmentPreviewDialog(true);
            setAttachmentFile(file);
            setNewAttachment(true);
        }, []);

        const removeAttachment = useCallback((attachmentId: any) => {
            const payload = {};
            CommonService._test.deleteTestAttachment(testID, attachmentId, payload)
                .then((response: IAPIResponseType<any>) => {
                    CommonService._alert.showToast(response?.message || 'attachments has been removed', "success");
                    setIsEdited(true);
                    setAttachmentList((oldState: any) => {
                        const updatedList = _.cloneDeep(oldState);
                        const index = updatedList.findIndex((item: any) => item.id === attachmentId);
                        updatedList.splice(index, 1);
                        return updatedList;
                    })
                })
                .catch((error: any) => {
                    CommonService._alert.showToast(error?.message || 'Error deleting attachment', "error");
                })
        }, [testID]);

        const handleAttachmentRemove = useCallback((id: any) => {
            CommonService.openConfirmationDialog({
                confirmationTitle: 'Please confirm!',
                confirmationSubTitle: 'Are you sure you want to delete this attachment?',
                disableEscKeyClose: false,
                disableBackdropClose: true,
                imagePath: `${ImageConfig.DeleteImage}`,
                direction: "up",
                yes: {
                    text: "Confirm",
                    color: "primary"
                },
                no: {
                    text: "Cancel",
                    color: "primary"
                }
            })
                .then(() => {
                    removeAttachment(id);
                })
                .catch(() => {
                })
        }, [removeAttachment]);


        return (
            <div className="test-attachment-screen">
                <div className="attachments-with-title-wrapper">
                    <form action="javacsript:void" noValidate={true}>
                        <div className="t-form-controls-wrapper">
                            <div className="attachment-section-header">Attachments</div>
                            <div className="attachment-input-wrapper">
                                <div className={'attachment-input-section'}>
                                    <InputComponent
                                        type={"text"}
                                        name={"title"}
                                        label={"Attachment Name"}
                                        placeholder={"Enter Attachment Name"}
                                        value={attachmentTitle}
                                        required={true}
                                        onChange={(value: any) => {
                                            setAttachmentTitle(value.replace(/\s/g, ''));
                                        }}
                                    />
                                    {
                                        attachmentTitle.length > 100 &&
                                        <ErrorComponent errorText={'Attachment name should be less than 100 characters'}/>
                                    }
                                </div>
                                <input type={"file"}
                                       className={"display-none"}
                                       ref={attachmentFileRef}
                                       accept={"image/jpeg,image/gif,image/png,application/pdf,image/x-eps"}
                                       onChange={(event) => {
                                           handleAttachmentUpload(event);
                                       }}
                                />
                                <ButtonComponent
                                    className="mrg-left-20"
                                    disabled={attachmentTitle?.length === 0 || attachmentTitle?.length > 100}
                                    color={"secondary"}
                                    type={'button'}
                                    variant={"contained"}
                                    fullWidth={false}
                                    size={'large'}
                                    isLoading={isAttachmentBeingUploaded}
                                    handleClick={() => {
                                        attachmentFileRef.current?.click()
                                    }}>
                                    Choose
                                </ButtonComponent>
                            </div>

                            <div className="attachment-list-section">
                                <>
                                    {attachmentList?.length > 0 &&
                                        <div className="attachment-list-wrapper">
                                            {attachmentList?.map((attachment: any) =>
                                                <div key={CommonService.getUUID()}>
                                                    <AttachmentViewDownloadComponent
                                                        attachment={attachment}
                                                        attachmentTitle={attachment.attachmentName}
                                                        showDeleteOption={true}
                                                        onClick={(id: any) => handleAttachmentRemove(id)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    }
                                    {attachmentList?.length === 0 &&
                                        <FeedbackComponent>No attachment have been uploaded.</FeedbackComponent>
                                    }
                                </>
                            </div>
                        </div>

                        <div className='form-action-section-edit'>
                            <ButtonComponent
                                className={"width-25"}
                                color={"secondary"}
                                type={"button"}
                                variant={"outlined"}
                                fullWidth={false}
                                handleClick={() => {
                                    onEditCancel()
                                }}
                            >
                                Cancel
                            </ButtonComponent>
                            <ButtonComponent
                                className="width-25"
                                type="button"
                                color={"primary"}
                                variant={"contained"}
                                fullWidth={false}
                                disabled={isAttachmentBeingUploaded || !isEdited}
                                handleClick={() => onUpdateComplete(attachmentList)}
                            >
                                Save
                            </ButtonComponent>
                        </div>
                    </form>
                </div>

                <DialogComponent open={showAttachmentPreviewDialog}
                                 maxWidth={'sm'}
                                 title={attachmentTitle}
                                 isShowCancel={true}
                                 onClose={() => {
                                     setShowAttachmentPreviewDialog(false);
                                     setAttachmentTitle("");
                                 }}
                                 className={"attachment-preview-dialog"}>
                    <div className="attachment-preview-dialog-body" id="file_preview_component">
                        {attachmentFile && attachmentFile?.type === 'application/pdf' ?
                            <PdfPreviewComponent
                                attachmentFile={attachmentFile}
                                width="550"
                                height="500"
                                isFile={isNewAttachment}
                            />
                            :
                            <FilePreviewerComponent
                                attachmentFile={attachmentFile}
                                isFile={isNewAttachment}/>
                        }
                    </div>
                    {attachmentFile && <div className="uploaded-attachment-file-name">
                        <span className="font-weight-bold"> Uploaded File Name:</span>
                        <span>{attachmentFile?.name}</span>
                    </div>
                    }
                    <div className="attachment-preview-dialog-footer">
                        <ButtonComponent variant={"outlined"}
                                         id="cancel_btn"
                                         fullWidth={true}
                                         color={'primary'}
                                         disabled={isAttachmentBeingUploaded}
                                         handleClick={() => {
                                             attachmentFileRef.current?.click()
                                         }}
                        >
                            Re-upload
                        </ButtonComponent>&nbsp;
                        {
                            isContinueVisible &&
                            <ButtonComponent variant={"contained"}
                                             id="continue_btn"
                                             color={'primary'}
                                             fullWidth={true}
                                             isLoading={isAttachmentBeingUploaded}
                                             handleClick={onAttachmentSubmit}>
                                Continue
                            </ButtonComponent>
                        }
                    </div>
                </DialogComponent>
            </div>
        )
    }

;

export default TestAttachmentsEditComponent;
